import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Progress,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { Announcement } from "core";
import { httpsCallable } from "firebase/functions";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useFunctions } from "reactfire";
import CreateAnnouncementModal from "./CreateAnnouncementModal";
import AnnouncementDetailModal from "./AnnouncementDetailModal";

type AnnouncementWithId = Announcement & { id: string };

export default function Announcements() {
  const { id: communityId } = useParams();
  const functions = useFunctions();

  const [announcements, setAnnouncements] = useState<AnnouncementWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnnouncement, setSelectedAnnouncement] =
    useState<AnnouncementWithId | null>(null);

  const {
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
    onClose: onCreateClose,
  } = useDisclosure();
  const {
    isOpen: isDetailOpen,
    onOpen: onDetailOpen,
    onClose: onDetailClose,
  } = useDisclosure();

  const fetchAnnouncements = async () => {
    if (!communityId) return;

    try {
      setLoading(true);
      const listAnnouncementsFn = httpsCallable(functions, "listAnnouncements");
      const result = await listAnnouncementsFn({ communityId });
      const data = result.data as {
        success: boolean;
        announcements: AnnouncementWithId[];
      };
      
      setAnnouncements(data.announcements || []);
    } catch (error) {
      console.error("Error fetching announcements:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, [communityId]);

  const handleViewDetails = (announcement: AnnouncementWithId) => {
    setSelectedAnnouncement(announcement);
    onDetailOpen();
  };

  const handleAnnouncementCreated = () => {
    fetchAnnouncements();
    onCreateClose();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "green";
      case "sending":
        return "blue";
      case "queued":
        return "orange";
      case "failed":
        return "red";
      default:
        return "gray";
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "—";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" h="200px">
        <Spinner size="lg" />
      </Flex>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Announcements</Heading>
        <Button colorScheme="blue" onClick={onCreateOpen}>
          Create Announcement
        </Button>
      </Flex>

      {announcements.length === 0 ? (
        <Box
          p={8}
          borderWidth={1}
          borderRadius="lg"
          textAlign="center"
          bg="gray.50"
        >
          <Text color="gray.600">
            No announcements yet. Create your first announcement to send bulk
            messages to your community.
          </Text>
        </Box>
      ) : (
        <Box borderWidth={1} borderRadius="lg" overflow="hidden">
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Created</Th>
                <Th>Message Preview</Th>
                <Th>Status</Th>
                <Th>Recipients</Th>
                <Th>Progress</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {announcements.map((announcement) => {
                const progress =
                  announcement.totalRecipients > 0
                    ? (announcement.sentCount / announcement.totalRecipients) *
                      100
                    : 0;

                return (
                  <Tr key={announcement.id}>
                    <Td>
                      <Text fontSize="sm">
                        {formatDate(announcement.createdAt)}
                      </Text>
                    </Td>
                    <Td maxW="300px">
                      <Text noOfLines={2} fontSize="sm">
                        {announcement.message}
                      </Text>
                    </Td>
                    <Td>
                      <Badge colorScheme={getStatusColor(announcement.status)}>
                        {announcement.status}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontSize="sm">
                        {announcement.totalRecipients}
                      </Text>
                    </Td>
                    <Td>
                      <VStack align="stretch" spacing={1}>
                        <HStack spacing={2} fontSize="xs" color="gray.600">
                          <Text>
                            {announcement.sentCount} sent
                          </Text>
                          {announcement.failedCount > 0 && (
                            <Text color="red.500">
                              {announcement.failedCount} failed
                            </Text>
                          )}
                        </HStack>
                        <Progress
                          value={progress}
                          size="sm"
                          colorScheme={
                            announcement.status === "completed"
                              ? "green"
                              : "blue"
                          }
                          borderRadius="md"
                        />
                      </VStack>
                    </Td>
                    <Td>
                      <Button
                        size="sm"
                        onClick={() => handleViewDetails(announcement)}
                      >
                        View Details
                      </Button>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      <CreateAnnouncementModal
        isOpen={isCreateOpen}
        onClose={onCreateClose}
        communityId={communityId!}
        onSuccess={handleAnnouncementCreated}
      />

      {selectedAnnouncement && (
        <AnnouncementDetailModal
          isOpen={isDetailOpen}
          onClose={onDetailClose}
          announcement={selectedAnnouncement}
        />
      )}
    </Box>
  );
}
